import {
  Affiliates,
  ClusterOperators,
  FirstPurchase,
  FirstPurchaseLogin,
  Installments,
  ItemPriceRange,
  ListPrice,
  ListPriceKeys,
  MarketingTags,
  OPERATORS,
  RnbCalculatorPayload,
  Subscription,
  SubscriptionCycle,
  SubscriptionCycleOperators,
  SubscriptionFrequency,
  SubscriptionType,
  UTMICampaign,
  ZIP_CODE_RANGE_OPERATOR,
  type BIN,
  type Cluster,
  type Conditions,
  type Item,
  type OrderValue,
  type ShippingLocation,
  type ShippingMethods,
  type UTM,
} from 'typings'

export function mapOrderValue(
  data: RnbCalculatorPayload
): OrderValue | undefined {
  const { totalValueMode, totalValueCeling, totalValueFloor } = data

  if (totalValueCeling === 0 && totalValueFloor === 0) return undefined

  return {
    min: totalValueFloor || undefined,
    max: totalValueCeling || undefined,
    itemsToConsider: totalValueMode,
  }
}

export function mapUTM(data: RnbCalculatorPayload): UTM | undefined {
  const { utmSource, utmCampaign } = data

  const source =
    utmSource
      ?.split(',')
      .map((utm) => utm.trim())
      .filter((x) => x.length) ?? []

  const campaign =
    utmCampaign
      ?.split(',')
      .map((utm) => utm.trim())
      .filter((x) => x.length) ?? []

  if (!source.length && !campaign.length) return undefined

  return {
    utmSource: source,
    utmCampaign: campaign,
  }
}

export function mapClusterExpressions(
  data: RnbCalculatorPayload
): Cluster | undefined {
  if (!data.clusterExpressions.length) return undefined

  return {
    expressions: data.clusterExpressions,
    operator:
      (data.clusterOperator?.toLocaleLowerCase() as ClusterOperators) ??
      ClusterOperators.ALL,
  }
}

export function mapPaymentMethods(
  data: RnbCalculatorPayload
): Item[] | undefined {
  const { paymentsMethods } = data

  return paymentsMethods.length ? paymentsMethods : undefined
}

export function mapBin(data: RnbCalculatorPayload): BIN | undefined {
  const { restrictionsBins } = data

  return restrictionsBins.length ? restrictionsBins : undefined
}

export function mapShippingMethods(
  data: RnbCalculatorPayload
): ShippingMethods | undefined {
  const {
    slasIds,
    percentualShippingDiscountValue,
    absoluteShippingDiscountValue,
  } = data

  if (percentualShippingDiscountValue > 0 || absoluteShippingDiscountValue > 0)
    return undefined

  return slasIds?.length ? slasIds.map((id) => ({ id, name: id })) : undefined
}

export function mapShippingLocation(
  data: RnbCalculatorPayload
): ShippingLocation | undefined {
  const { countries, zipCodeRanges: ranges } = data

  if (!countries?.length && !ranges?.length) return undefined

  const shippingCountries = countries ?? []

  const zipCodeRanges =
    ranges?.map(({ zipCodeFrom, zipCodeTo, inclusive }) => ({
      from: zipCodeFrom,
      to: zipCodeTo,
      operator: inclusive
        ? ZIP_CODE_RANGE_OPERATOR.INCLUDE
        : ZIP_CODE_RANGE_OPERATOR.EXCLUDE,
    })) ?? []

  return {
    shippingCountries,
    zipCodeRanges,
  }
}

export function mapHistoricalPurchaseValue(
  data: RnbCalculatorPayload
): number | undefined {
  const { totalValuePurchase } = data

  return totalValuePurchase || undefined
}

export function mapListPrice(data: RnbCalculatorPayload): ListPrice {
  const { compareListPriceAndPrice, isDifferentListPriceAndPrice } = data

  if (compareListPriceAndPrice === isDifferentListPriceAndPrice)
    return undefined

  return isDifferentListPriceAndPrice
    ? ListPriceKeys.DEFINED
    : ListPriceKeys.NOT_DEFINED
}

export function mapItemPriceRange(
  data: RnbCalculatorPayload
): ItemPriceRange | undefined {
  const min = data.itemMinPrice || undefined // zero is considered not defined
  const max = data.itemMaxPrice || undefined

  if (!min && !max) return undefined

  return {
    min,
    max,
  }
}

export function mapAffiliates(data: RnbCalculatorPayload): Affiliates {
  const { affiliates, affiliatesIsInclusive } = data

  if (!affiliates.length) return undefined

  return {
    affiliates,
    operator:
      affiliatesIsInclusive === false ? OPERATORS.DIFFERENT : OPERATORS.EQUALS,
  }
}

export function mapInstallments(data: RnbCalculatorPayload): Installments {
  const { maxInstallment, minInstallment } = data

  if (!maxInstallment && !minInstallment) return undefined
  if (!minInstallment) {
    return { max: maxInstallment, min: undefined }
  }
  if (!maxInstallment) return { min: minInstallment, max: undefined }
  return { min: minInstallment, max: maxInstallment }
}

export function mapMarketingTags(
  data: RnbCalculatorPayload
): MarketingTags | undefined {
  const { marketingTags, marketingTagsAreNotInclusive } = data

  if (!marketingTags || !marketingTags.length) return undefined

  return {
    operator: marketingTagsAreNotInclusive
      ? OPERATORS.DIFFERENT
      : OPERATORS.EQUALS,
    tags: marketingTags,
  }
}

export function mapFirstPurchase(
  data: RnbCalculatorPayload
): FirstPurchase | undefined {
  const { isFirstBuy, firstBuyIsProfileOptimistic } = data

  if (!isFirstBuy) return undefined

  return {
    login: firstBuyIsProfileOptimistic
      ? FirstPurchaseLogin.OPTIONAL
      : FirstPurchaseLogin.REQUIRED,
  }
}

export function mapSubscriptionType(
  subscriptions: NonNullable<RnbCalculatorPayload['subscriptions']>
): SubscriptionType {
  const { firstOrders, recurrentOrders } = subscriptions.execution

  if (firstOrders && recurrentOrders) return SubscriptionType.ALL
  if (firstOrders) return SubscriptionType.FIRST
  return SubscriptionType.RECURRING
}

export function mapSubscriptionCycle(
  subscriptions: NonNullable<RnbCalculatorPayload['subscriptions']>
): SubscriptionCycle | undefined {
  const { recurrentOrders } = subscriptions.execution

  if (!recurrentOrders) return undefined

  const { minCount, maxCount } = recurrentOrders

  if (minCount && maxCount && minCount === maxCount)
    return { operator: SubscriptionCycleOperators.EQUALS, value: minCount }

  if (minCount && maxCount)
    return {
      operator: SubscriptionCycleOperators.RANGE,
      max: maxCount,
      min: minCount,
    }

  if (maxCount)
    return { operator: SubscriptionCycleOperators.LESS_THAN, value: maxCount }

  if (minCount)
    return {
      operator: SubscriptionCycleOperators.GREATER_THAN,
      value: minCount,
    }
}

export function mapSubscriptionFrequency(
  subscriptions: NonNullable<RnbCalculatorPayload['subscriptions']>
): SubscriptionFrequency | undefined {
  const { plan } = subscriptions

  if (!plan) return undefined

  const { interval, periodicity } = plan.frequency

  return {
    unit: periodicity,
    value: interval,
  }
}

export function mapSubscription(
  data: RnbCalculatorPayload
): Subscription | undefined {
  const { subscriptions } = data

  if (!subscriptions) return undefined

  return {
    type: mapSubscriptionType(subscriptions),
    filter: {
      frequency: mapSubscriptionFrequency(subscriptions),
      cycle: mapSubscriptionCycle(subscriptions),
    },
  }
}

export function mapUTMICampaign(
  data: RnbCalculatorPayload
): UTMICampaign | undefined {
  const { utmiCampaign } = data

  const processedUtm =
    utmiCampaign
      ?.split(',')
      .map((utm) => utm.trim())
      .filter((x) => x.length) ?? []

  return processedUtm.length ? { utmiCampaign: processedUtm } : undefined
}

export function mapConditions(data: RnbCalculatorPayload): Conditions {
  return {
    paymentMethods: mapPaymentMethods(data),
    utm: mapUTM(data),
    cluster: mapClusterExpressions(data),
    bin: mapBin(data),
    shippingMethods: mapShippingMethods(data),
    shippingLocation: mapShippingLocation(data),
  }
}
